import { useState } from "react";
import { toast } from "../../../utils/toast";
import { Button, FileUpload, InfoBox, Input, Modal, Select, Textarea } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf } from "../../../api/scholarships";
import { kigaliDate } from "../model";
import { formatMoney } from "../funding";
import { paymentKindsFor } from "./model";

const MAX_TEXT = 100;
const MAX_NOTE = 500;
const today = () => kigaliDate(new Date().toISOString());

// One dialog's busy state, field errors and a call that keeps both up to date
const useSubmit = (onDone, what) => {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const submit = async (work, success) => {
    setBusy(true);
    setErrors({});
    try {
      const { data } = await work();
      toast.success(success);
      onDone(data);
    } catch (error) {
      console.error(`Failed to ${what}:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, errors, setErrors, submit };
};

const footer = (busy, onClose, onSave, label) => (
  <>
    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
      Cancel
    </Button>
    <Button size="sm" onClick={onSave} loading={busy}>
      {label}
    </Button>
  </>
);

/** BRD uploads the ticket it bought, with the flight it is for; a new upload replaces the old one. `source` as on LoanPaymentPage. */
export function TicketDialog({ loan, source, onClose, onDone }) {
  const current = loan.travel.ticket;
  const [form, setForm] = useState({ airline: current?.airline ?? "", flightNumber: current?.flightNumber ?? "", departureAt: current?.departureAt?.slice(0, 16) ?? "" });
  const [file, setFile] = useState(null);
  const { busy, errors, setErrors, submit } = useSubmit(onDone, `upload the ticket of application ${loan.applicationId}`);
  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: busy,
  });

  const save = () => {
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => body.append(key, value.trim()));
    if (file) body.append("file", file);
    submit(() => source.uploadTicket(loan.applicationId, body), current ? "Ticket replaced" : "Ticket uploaded");
  };

  return (
    <Modal open onClose={onClose} size="md" dismissible={!busy} title={current ? "Replace the ticket" : "Upload the ticket"} footer={footer(busy, onClose, save, "Upload")}>
      <div className="grid gap-4 md:grid-cols-2">
        <Input label="Airline" required maxLength={MAX_TEXT} {...field("airline")} />
        <Input label="Flight number" required maxLength={MAX_TEXT} {...field("flightNumber")} />
        <Input type="datetime-local" label="Departure" required className="md:col-span-2" {...field("departureAt")} />
        <FileUpload label="Ticket" required className="md:col-span-2" accept="application/pdf,image/jpeg,image/png" hint="PDF, JPEG or PNG, up to 10 MB" file={file} onChange={setFile} error={errors.file} />
      </div>
    </Modal>
  );
}

/**
 * BRD records a payment: the tuition paid to the university (the institution in Rwanda), or an
 * allowance sent to the embassy (the student in Rwanda). Tuition beyond the approved fee is allowed,
 * since fees change, but the dialog says so first.
 */
export function PaymentDialog({ loan, source, onClose, onDone }) {
  const currency = loan.approvedFunding.currency;
  const [form, setForm] = useState({ kind: "", amount: "", paidOn: today(), reference: "", note: "" });
  const { busy, errors, setErrors, submit } = useSubmit(onDone, `record a payment on application ${loan.applicationId}`);
  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: busy,
  });

  const tuitionLeft = (loan.approvedFunding.tuitionFee ?? 0) - (loan.tuitionPaid ?? 0);
  const overpaid = form.kind === "TUITION" && Number(form.amount) > tuitionLeft;

  const save = () =>
    submit(
      () =>
        source.recordPayment(loan.applicationId, {
          kind: form.kind,
          amount: form.amount === "" ? null : Number(form.amount),
          paidOn: form.paidOn,
          reference: form.reference.trim(),
          note: form.note.trim() || null,
        }),
      "Payment recorded"
    );

  return (
    <Modal open onClose={onClose} size="md" dismissible={!busy} title="Record a payment" footer={footer(busy, onClose, save, "Record")}>
      <div className="grid gap-4 md:grid-cols-2">
        <Select label="Payment" required placeholder="Choose" options={paymentKindsFor(loan.travels)} className="md:col-span-2" {...field("kind")} />
        <Input type="number" min={1} step={1} label="Amount" required suffix={currency} {...field("amount")} />
        <Input type="date" label="Paid on" required max={today()} {...field("paidOn")} />
        <Input label="Reference" required maxLength={MAX_TEXT} className="md:col-span-2" {...field("reference")} />
        <Textarea label="Note (optional)" rows={2} maxLength={MAX_NOTE} className="md:col-span-2" {...field("note")} />
        {overpaid && (
          <InfoBox className="md:col-span-2">
            This is more than the tuition left to pay ({formatMoney(Math.max(tuitionLeft, 0), currency)} of the approved {formatMoney(loan.approvedFunding.tuitionFee, currency)}). Check the amount, or say why in the note.
          </InfoBox>
        )}
      </div>
    </Modal>
  );
}
